import React, { useContext, useState, useEffect } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { AppContext } from "../../../../context/AppContext";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import ApiConfig from "../../../../api/ApiConfig";
import { simplePostCall } from "../../../../api/ApiServices";
import Loader from "../../../../sharedComponent/Loader";
import CommonDatePicker from "../../../../sharedComponent/CommonDatePicker";
import { latestDate } from "../../../../sharedComponent/common";
import { Select } from "antd";

const { Option } = Select;

/* === GREEN :: UI UPDATED (map + trip dropdown + legend) === */
const CenterMap = ({ center }) => {
  const map = useMap();
  const isValid =
    Array.isArray(center) &&
    center.length === 2 &&
    !isNaN(center[0]) &&
    !isNaN(center[1]);
  useEffect(() => {
    if (isValid) map?.setView(center, map?.getZoom());
  }, [center, map, isValid]);
  return null;
};

const qualityColor = (q) => {
  const v = String(q || "").toUpperCase();
  if (v === "G" || v === "GOOD" || v === "4") return "#21c45d";
  if (v === "A" || v === "F" || v === "AVERAGE" || v === "FAIR" || v === "3")
    return "#ffd200";
  if (v === "P" || v === "POOR" || v === "2") return "#ff8a1f";
  return "#ff3b30";
};

const qualityLabel = (q) => {
  const v = String(q || "").toUpperCase();
  if (v === "G" || v === "GOOD" || v === "4") return "Good";
  if (v === "A" || v === "F" || v === "AVERAGE" || v === "FAIR" || v === "3")
    return "Average";
  if (v === "P" || v === "POOR" || v === "2") return "Poor";
  return "Very Poor";
};

const getMarkerIcon = (q) =>
  L.divIcon({
    className: "custom-icon",
    html: `
      <div style="
        width: 14px; height: 14px;
        background-color: ${qualityColor(q)};
        border-radius: 50%;
        border: 1px solid #222;
      "></div>
    `,
    iconSize: [14, 14],
    iconAnchor: [7, 14],
  });

const GPSStrengthReport = () => {
  const { sidebar } = useContext(AppContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [tripList, setTripList] = useState([]);
  const [tripId, setTripId] = useState("");
  const [tripName, setTripName] = useState("");
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [reportDate, setReportDate] = useState({ toDayDate: new Date() });
  const [locations, setLocations] = useState([]);
  const maxDate = new Date();

  const aninations = {
    initial: { opacity: 0, x: 400 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 100 },
  };

  useEffect(() => {
    getTripList();
  }, []);

  /* === GREEN :: trip dropdown populated from trip-list report (shows trip names) === */
  const getTripList = () => {
    simplePostCall(ApiConfig.TRIP_LIST_REPORT)
      .then((res) => {
        setTripList(res?.listData || []);
      })
      .catch((err) => console.log("trip list err", err));
  };

  /* === GREEN :: API INTEGRATED (GPS report = vehicle of selected trip + date) === */
  const fetchReport = () => {
    if (!tripId) return;
    const body = {
      format: "",
      vehicle_id: selectedVehicleId,
      report_date: latestDate(reportDate.toDayDate, "yyyy-MM-dd"),
    };
    setLoading(true);
    simplePostCall(ApiConfig.GPS_STRENGTH_REPORT, JSON.stringify(body))
      .then((res) => {
        if (res?.result) setLocations(res?.listData || []);
        else setLocations([]);
      })
      .catch((err) => console.log("Error:", err))
      .finally(() => setLoading(false));
  };

  const hasLocations = locations?.length > 0;
  const center = hasLocations
    ? [parseFloat(locations[0].latitude), parseFloat(locations[0].longitude)]
    : [25.2854, 51.531];

  return (
    <motion.div
      className={sidebar ? "taskMain " : "cx-active taskMain"}
      id="cx-main"
      variants={aninations}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.1 }}
    >
      <div id="cx-wrapper" className="Vehicle_inspition_dashboard heightFixed">
        <div className="report-viwe-head" id="Viwe_Report_Respoasive">
          <p></p>
        </div>

        <div className="col-lg-12 mt-0">
          <div className="UserTabel">
            {/* === GREEN :: filter row (single trip dropdown + Search) === */}
            <div className="heading">
              <div className="multi-select-1" style={{ flex: 1 }}>
                <Select
                  style={{ height: "40px", width: "100%" }}
                  className="custom-select"
                  placeholder={t("Select Trip")}
                  value={tripId || undefined}
                  onChange={(value, option) => {
                    setTripId(value);
                    setTripName(option?.label || "");
                    const sel = tripList.find(
                      (t) => String(t.trip_id) === String(value)
                    );
                    setSelectedVehicleId(
                      sel?.trip_vehicle_id || sel?.vehicle_id || ""
                    );
                  }}
                  showSearch
                  allowClear
                  filterOption={(input, option) =>
                    option?.label
                      ?.toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  optionFilterProp="label"
                >
                  {tripList?.map((tr) => {
                    const label =
                      tr.trip_name ||
                      (tr.trip_id ? `Trip ${tr.trip_id}` : tr.vehicle_number);
                    return (
                      <Option
                        key={tr.trip_id}
                        value={tr.trip_id}
                        label={label}
                        style={{ color: "rgba(156, 73, 0)" }}
                      >
                        {label}
                      </Option>
                    );
                  })}
                </Select>
              </div>

              <div className="innerSelectBox weekCounter datepicker-main">
                <CommonDatePicker
                  dateKey={"toDayDate"}
                  setDate={setReportDate}
                  data={reportDate}
                  SetPlaceholder={t("Report Date")}
                  dataDisbal={maxDate}
                />
              </div>

              <div className="innerSelectBox">
                <button
                  type="button"
                  className="btn btn-success"
                  style={{ height: "40px", minWidth: "100px" }}
                  onClick={fetchReport}
                >
                  {t("Search")}
                </button>
              </div>
            </div>

            {/* === GREEN :: trip name display === */}
            {tripName && (
              <div className="m-2">
                <strong>{t("Trip Name")}</strong> {tripName}
              </div>
            )}

            {/* === GREEN :: legend === */}
            <div className="row m-2">
              <div className="col-auto d-flex align-items-center">
                <span
                  style={{
                    display: "inline-block",
                    width: 14,
                    height: 14,
                    background: "#ff3b30",
                    marginRight: 6,
                    border: "1px solid #222",
                  }}
                ></span>
                {t("Very Poor")}
              </div>
              <div className="col-auto d-flex align-items-center">
                <span
                  style={{
                    display: "inline-block",
                    width: 14,
                    height: 14,
                    background: "#ff8a1f",
                    marginRight: 6,
                    border: "1px solid #222",
                  }}
                ></span>
                {t("Poor")}
              </div>
              <div className="col-auto d-flex align-items-center">
                <span
                  style={{
                    display: "inline-block",
                    width: 14,
                    height: 14,
                    background: "#ffd200",
                    marginRight: 6,
                    border: "1px solid #222",
                  }}
                ></span>
                {t("Average")}
              </div>
              <div className="col-auto d-flex align-items-center">
                <span
                  style={{
                    display: "inline-block",
                    width: 14,
                    height: 14,
                    background: "#21c45d",
                    marginRight: 6,
                    border: "1px solid #222",
                  }}
                ></span>
                {t("Good")}
              </div>
            </div>

            {/* === GREEN :: map (replaces table) === */}
            <div className="m-2">
              {loading ? (
                <Loader />
              ) : (
                <MapContainer
                  center={center}
                  zoom={12}
                  scrollWheelZoom={true}
                  style={{ height: "70vh", width: "100%" }}
                >
                  <CenterMap center={center} />
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {locations?.map((loc, index) => {
                    const lat = parseFloat(loc.latitude);
                    const lng = parseFloat(loc.longitude);
                    if (isNaN(lat) || isNaN(lng)) return null;
                    return (
                      <Marker
                        key={index}
                        position={[lat, lng]}
                        icon={getMarkerIcon(loc.gps_quality)}
                      >
                        <Popup>
                          <div>
                            <strong>{t("GPS Quality")}:</strong>{" "}
                            {qualityLabel(loc.gps_quality)} ({loc.gps_quality})
                            <br />
                            <strong>{t("Latitude")}:</strong> {loc.latitude}{" "}
                            {loc.latitude_direction}
                            <br />
                            <strong>{t("Longitude")}:</strong> {loc.longitude}{" "}
                            {loc.longitude_direction}
                            <br />
                            <strong>{t("Logged Time")}:</strong>{" "}
                            {loc.logged_time}
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
                </MapContainer>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default GPSStrengthReport;
